import React, { useEffect, useMemo, useRef } from "react";
import { Bell, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import type { MarkedDay } from "@/features/app-context/types";
import type { PendingBreakfastInvite } from "@/features/breakfast/types";
import type { DBNotification } from "../types";
import { AttendanceCard } from "./AttendanceCard";
import { BreakfastInviteCard } from "./BreakfastInviteCard";
import { EmptyState } from "./EmptyState";
import { NotifRow } from "./NotifRow";
import type { NotifPanelActions, NotifPanelLoading } from "./panel-types";
import type { SenderMap } from "../hooks/useNotifSenders";
import { getVisibleInboxItemCount } from "../lib/notificationCounts";

interface NotificationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  activeTab: "inbox" | "history";
  onTabChange: (tab: "inbox" | "history") => void;

  badgeCount: number;

  // ── Inbox tab data ────────────────────────────────────────────
  inbox: DBNotification[];
  daysToConfirm: MarkedDay[];
  pendingInvites: PendingBreakfastInvite[];
  rescheduledEventIds: Set<string>;
  /** DB inbox rows whose event is already covered by an invite card. */
  coveredNotifIds: Set<string>;

  /** "Archive all" button state + handler. */
  isClearingAllInbox: boolean;
  onClearAllInbox: () => void;
  /**
   * Increments on successful "Archive all" so the sheet can move keyboard
   * focus to the History tab (announced via the live region).
   */
  inboxClearedFocusSignal: number;

  // ── History tab data ──────────────────────────────────────────
  history: DBNotification[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  isBulkDeleting: boolean;
  onBulkDelete: (ids: string[]) => void;

  /** Resolved profile rows keyed by `notif.sender_id`. */
  senders: SenderMap;
  /**
   * Optional notification id the sheet should scroll to and pulse on
   * open. Flows in from a popover row click or a URL deep-link.
   */
  highlightId: string | null;

  actions: NotifPanelActions;
  loading: NotifPanelLoading;
}

/**
 * Full-screen Notification Centre sheet (right-side slide-in).
 *
 * Houses two tabs:
 *
 *   • Inbox   — live, polled list of `is_archived = false` rows, plus
 *               pending breakfast invite cards and past-day attendance
 *               confirmations. De-duped so a single breakfast event
 *               never shows as both an invite card AND a notification
 *               row (the first-class card wins; the row is filtered
 *               out via `coveredNotifIds`).
 *
 *   • History — lazy list of archived rows, with multi-select +
 *               bulk-delete. The "select all" + per-row checkbox are
 *               driven by a `Set<string>` lifted into the orchestrator
 *               so toggling back to Inbox clears selection.
 *
 * The sheet exists as a distinct surface from the popover to provide
 * full actions (Mark read / Archive / Delete / Restore) on each row —
 * those hover-reveal buttons only render in `compact={false}` mode.
 *
 * Accessibility: a polite `role="status"` live region keeps screen
 * readers in sync with inbox/history context. After a successful
 * “Archive all”, focus moves to the History tab.
 */
export function NotificationSheet({
  open,
  onOpenChange,
  activeTab,
  onTabChange,
  badgeCount,
  inbox,
  daysToConfirm,
  pendingInvites,
  rescheduledEventIds,
  coveredNotifIds,
  isClearingAllInbox,
  onClearAllInbox,
  inboxClearedFocusSignal,
  history,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  isBulkDeleting,
  onBulkDelete,
  senders,
  highlightId,
  actions,
  loading,
}: NotificationSheetProps) {
  const historyTabRef = useRef<HTMLButtonElement | null>(null);
  const lastClearedRef = useRef(0);

  const inboxNonEmptyCount = useMemo(
    () =>
      getVisibleInboxItemCount(
        inbox,
        coveredNotifIds,
        daysToConfirm.length,
        pendingInvites.length,
      ),
    [inbox, coveredNotifIds, daysToConfirm.length, pendingInvites.length],
  );

  const liveMessage = useMemo(() => {
    if (activeTab === "inbox") {
      const queue =
        inboxNonEmptyCount === 0
          ? "Inbox, all caught up, nothing in your queue."
          : `Inbox, ${inboxNonEmptyCount} ${
              inboxNonEmptyCount === 1 ? "item" : "items"
            } in your queue.`;
      const attention =
        badgeCount > 0
          ? `${badgeCount} ${
              badgeCount === 1 ? "item needs" : "items need"
            } attention.`
          : "Nothing needs attention right now.";
      return `Notification Centre. ${queue} ${attention}`;
    }
    return `Notification Centre. History, ${history.length} archived notification${
      history.length === 1 ? "" : "s"
    }.`;
  }, [activeTab, inboxNonEmptyCount, history.length, badgeCount]);

  useEffect(() => {
    if (inboxClearedFocusSignal <= 0) return;
    if (inboxClearedFocusSignal === lastClearedRef.current) return;
    lastClearedRef.current = inboxClearedFocusSignal;
    onTabChange("history");
    const id = requestAnimationFrame(() => {
      historyTabRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [inboxClearedFocusSignal, onTabChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-md p-0 flex flex-col gap-0 overflow-hidden"
      >
        <SheetDescription className="sr-only">
          View and manage your recent notifications.
        </SheetDescription>

        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {open ? liveMessage : ""}
        </p>

        {/* Sheet header */}
        <SheetHeader className="flex-row items-center gap-3 px-5 py-4 border-b border-border/60 flex-shrink-0 space-y-0">
          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Bell className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <SheetTitle className="text-sm font-semibold leading-tight">
              Notification Centre
            </SheetTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {badgeCount > 0
                ? `${badgeCount} item${badgeCount > 1 ? "s" : ""} need${badgeCount === 1 ? "s" : ""} attention`
                : "Nothing pending"}
            </p>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => onTabChange(v as "inbox" | "history")}
          className="flex flex-col flex-1 min-h-0"
        >
          <TabsList className="mx-5 mt-3 mb-0 self-start h-8 p-0.5">
            <TabsTrigger value="inbox" className="h-7 text-xs px-3 gap-1.5">
              Inbox
              {badgeCount > 0 && (
                <Badge
                  variant="destructive"
                  className="h-4 min-w-[1.1rem] px-1 text-[9px] font-bold"
                >
                  {badgeCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              ref={historyTabRef}
              value="history"
              className="h-7 text-xs px-3"
            >
              History
            </TabsTrigger>
          </TabsList>

          {/* ── INBOX TAB ─────────────────────────────────────── */}
          <TabsContent
            value="inbox"
            className="flex-1 overflow-y-auto mt-3 data-[state=inactive]:hidden"
          >
            {/* Toolbar */}
            {inboxNonEmptyCount > 0 && (
              <div className="px-5 pb-3 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {inboxNonEmptyCount} notification{inboxNonEmptyCount > 1 ? "s" : ""}
                </p>
                <button
                  onClick={onClearAllInbox}
                  disabled={isClearingAllInbox}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isClearingAllInbox ? "Archiving…" : "Archive all"}
                </button>
              </div>
            )}

            <div className="divide-y divide-border/40">
              {/* Pending breakfast invites (RSVP Center) — rendered first
                  so guests always see actionable RSVPs at the top of the
                  inbox. Sourced from `breakfast_attendees`, not the
                  `notifications` table, which means these cards appear
                  even if the upstream invite notification was never sent. */}
              {pendingInvites.map((invite) => (
                <BreakfastInviteCard
                  key={invite.id}
                  invite={invite}
                  compact={false}
                  rescheduled={rescheduledEventIds.has(invite.id)}
                  isLoading={loading.breakfastInviteEventId === invite.id}
                  onAccept={actions.onAcceptBreakfastInvite}
                  onDecline={actions.onDeclineBreakfastInvite}
                />
              ))}

              {/* Dynamic attendance items (in-memory, from AppContext) */}
              {daysToConfirm.map((item) => (
                <AttendanceCard
                  key={item.date}
                  dateStr={item.date}
                  compact={false}
                  isLoading={loading.attendanceDate === item.date}
                  onValidate={actions.onValidateAttendance}
                  onClear={actions.onClearAttendance}
                />
              ))}

              {/* DB inbox items (is_archived === false). Anything whose
                  event is already surfaced by a `BreakfastInviteCard`
                  above is filtered out so the guest doesn't see the
                  same event twice with two competing Accept buttons. */}
              {inbox
                .filter((n) => !coveredNotifIds.has(n.id))
                .map((notif) => (
                  <NotifRow
                    key={notif.id}
                    notif={notif}
                    compact={false}
                    inHistory={false}
                    sender={notif.sender_id ? senders.get(notif.sender_id) : undefined}
                    highlighted={highlightId === notif.id}
                    onMarkRead={actions.onMarkRead}
                    onArchive={actions.onArchive}
                    onDelete={(id) => actions.onDelete(id, false)}
                    onUnarchive={actions.onUnarchive}
                    onAcceptInvite={actions.onAcceptWorkspaceInvite}
                    onDeclineInvite={actions.onDeclineWorkspaceInvite}
                    onAcceptBuddy={actions.onAcceptBuddy}
                    onDeclineBuddy={actions.onDeclineBuddy}
                    onAcceptBreakfast={actions.onAcceptBreakfast}
                    onDeclineBreakfast={actions.onDeclineBreakfast}
                    onRemoveFromCalendar={actions.onRemoveFromCalendar}
                    onConfirmAttendance={(n) => {
                      // We need to parse the date from the notification.
                      // Since DAILY_REMINDER is sent on the day it applies to,
                      // we can just use its created_at date (truncated to YYYY-MM-DD).
                      actions.onValidateAttendance(n.created_at.slice(0, 10));
                      // We also want to mark the notification as read or archive it.
                      actions.onArchive(n.id);
                    }}
                    onClearAttendance={(n) => {
                      actions.onClearAttendance(n.created_at.slice(0, 10));
                      actions.onArchive(n.id);
                    }}
                  />
                ))}

              {inboxNonEmptyCount === 0 && <EmptyState tab="inbox" />}
            </div>
          </TabsContent>

          {/* ── HISTORY TAB ───────────────────────────────────── */}
          <TabsContent
            value="history"
            className="flex-1 overflow-y-auto mt-3 data-[state=inactive]:hidden flex flex-col"
          >
            {/* Bulk action toolbar */}
            {history.length > 0 && (
              <div className="px-5 pb-3 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={
                      selectedIds.size === history.length && history.length > 0
                    }
                    onCheckedChange={(v) =>
                      v ? onSelectAll() : onClearSelection()
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    {selectedIds.size > 0
                      ? `${selectedIds.size} selected`
                      : `${history.length} archived`}
                  </span>
                </div>
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 text-xs gap-1.5"
                    disabled={isBulkDeleting}
                    onClick={() => onBulkDelete([...selectedIds])}
                  >
                    <Trash2 className="h-3 w-3" />
                    {isBulkDeleting
                      ? "Deleting…"
                      : `Delete ${selectedIds.size}`}
                  </Button>
                )}
              </div>
            )}

            <div className="divide-y divide-border/40 flex-1">
              {history.length === 0 ? (
                <EmptyState tab="history" />
              ) : (
                history.map((notif) => (
                  <NotifRow
                    key={notif.id}
                    notif={notif}
                    compact={false}
                    inHistory
                    sender={notif.sender_id ? senders.get(notif.sender_id) : undefined}
                    checked={selectedIds.has(notif.id)}
                    onCheck={onToggleSelect}
                    onMarkRead={actions.onMarkRead}
                    onArchive={actions.onArchive}
                    onDelete={(id) => actions.onDelete(id, true)}
                    onUnarchive={actions.onUnarchive}
                  />
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
